import logger from '@/utils/logger.js'
import { db, storage } from '@/firebase.js'
import { onSnapshot, doc, deleteDoc, updateDoc, setDoc, arrayUnion } from 'firebase/firestore'
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage'
import debounce from 'lodash/debounce'

// Gemeinsame Charakter-Logik für die vier CharTab-Komponenten (DSA, D&D,
// Shadowrun, Götterlicht). Bündelt die früher 4× kopierte Firestore-Plumbing:
// ID-Auflösung, onSnapshot-Abo + Cleanup, Notiz-Autosave, Löschen, Freigaben
// und Datei-Upload. System-spezifische Felder kommen über applyCharData() rein,
// das Speichern der Stammdaten bleibt in der jeweiligen Komponente (saveChar).
export default {
  data() {
    return {
      unsubscribeActiveChar: null,
      Name: '',
      System: '',
      User: '',
      CreatedAt: '',
      csWebhook: '',
      Notizen: '',
      Notizen1: '',
      Notizen2: '',
      Notizen3: '',
      neueFreigabe: '',
      fileHandle: null,
      charPDFurl: '',
      charPicurl: '',
    }
  },

  computed: {
    ro() {
      return this.$route.query.ro === '1'
    },
    user() {
      return this.$store.state.User
    },
    activeChar() {
      return this.$store.state.activeChar
    },
  },

  watch: {
    Notizen(newVal, oldVal) { this.autosaveNotizen(newVal, oldVal) },
    Notizen1(newVal, oldVal) { this.autosaveNotizen(newVal, oldVal) },
    Notizen2(newVal, oldVal) { this.autosaveNotizen(newVal, oldVal) },
    Notizen3(newVal, oldVal) { this.autosaveNotizen(newVal, oldVal) },
  },

  mounted() {
    this.loadChar()
  },

  beforeUnmount() {
    if (this.unsubscribeActiveChar) {
      this.unsubscribeActiveChar()
    }
  },

  methods: {
    autosaveNotizen(newVal, oldVal) {
      if (newVal !== oldVal && this.$store.state.Einstellungen?.autosaveN) {
        this.saveNotizenDebounced()
      }
    },

    loadChar() {
      if (this.unsubscribeActiveChar) {
        this.unsubscribeActiveChar()
      }

      const charId = this.$route.query.id || this.$store.state.activeCharID || localStorage.getItem('activeCharID')
      if (!charId) {
        this.$router.push({ name: 'home' })
        logger.log("activeCharID ist nicht gesetzt. Umgeleitet an home.")
        return
      }
      if (this.$store.state.activeCharID !== charId) {
        this.$store.commit('setState', { key: 'activeCharID', value: charId })
      }
      localStorage.setItem('activeCharID', charId)

      const docRef = doc(db, "Charaktere", charId)

      this.unsubscribeActiveChar = onSnapshot(docRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data()
          this.$store.commit('setState', { key: 'activeChar', value: { id: docSnapshot.id, ...data } })

          // Gemeinsame Felder
          this.Name = data.Name
          this.System = data.System
          this.User = data.User
          this.CreatedAt = data.CreatedAt
          this.csWebhook = data.csWebhook
          this.Notizen = data.Notizen ?? ''
          this.Notizen1 = data.Notizen1 ?? ''
          this.Notizen2 = data.Notizen2 ?? ''
          this.Notizen3 = data.Notizen3 ?? ''
          this.charPDFurl = data.pdfURL ?? ''
          this.charPicurl = data.picURL ?? ''

          // System-spezifische Felder (von der jeweiligen Komponente gesetzt)
          this.applyCharData(data)
        } else {
          logger.log("Kein Dokument mit dieser ID gefunden.")
          this.$store.commit('setState', { key: 'activeChar', value: null })
        }
      }, (error) => {
        console.error("Fehler beim Empfangen des Charakters:", error)
      })
    },

    saveNotizenDebounced: debounce(function () {
      this.saveNotizen()
    }, 1000),

    saveNotizen() {
      if (!this.$store.state.activeCharID) {
        console.error("Keine activeCharID verfügbar.")
        return
      }
      try {
        const docRef = doc(db, "Charaktere", this.$store.state.activeCharID)
        setDoc(docRef, {
          Notizen: this.Notizen,
          Notizen1: this.Notizen1,
          Notizen2: this.Notizen2,
          Notizen3: this.Notizen3,
        }, { merge: true })
        this.$store.commit('callSnackbar', "Notiz gespeichert.")
      } catch (err) {
        console.error("Fehler beim Speichern der Notiz: ", err)
      }
    },

    async deleteChar() {
      if (!this.$store.state.activeCharID) {
        console.error("Keine activeCharID verfügbar.")
        return
      }
      try {
        const docRef = doc(db, "Charaktere", this.$store.state.activeCharID)
        await deleteDoc(docRef)
        this.$router.push({ name: 'home' })
        this.$store.commit('callSnackbar', this.Name + " wurde gelöscht.")
      } catch (err) {
        console.error("Fehler beim Löschen des Charakters: ", err)
      }
    },

    addoUser() {
      logger.log("addoUser triggered für: " + this.neueFreigabe)

      if (!this.$store.state.activeCharID) {
        console.error("Keine activeCharID verfügbar.")
        return
      }

      try {
        const docRef = doc(db, "Charaktere", this.$store.state.activeCharID)
        updateDoc(docRef, {
          oUsers: arrayUnion(this.neueFreigabe)
        })
        this.$store.commit('callSnackbar', this.neueFreigabe + " wurde hinzugefügt.")
      } catch (err) {
        console.error("Fehler beim Hinzufügen eines neuen oUsers: ", err)
      }
    },

    deleteoUsers() {
      logger.log("deleteoUsers triggered")

      if (!this.$store.state.activeCharID) {
        console.error("Keine activeCharID verfügbar.")
        return
      }

      try {
        const docRef = doc(db, "Charaktere", this.$store.state.activeCharID)
        updateDoc(docRef, {
          oUsers: []
        })
        this.$store.commit('callSnackbar', "Alle freigaben für diesen Charakter wurden gelöscht.")
      } catch (err) {
        console.error("Fehler beim Löschen der oUsers: ", err)
      }
    },

    async fileHandler(Dateiart) {
      if (!this.fileHandle) return

      const file = this.fileHandle
      const fileRef = storageRef(storage, `${this.$store.state.activeCharID}/${file.name}`)
      try {
        await uploadBytes(fileRef, file)
        const url = await getDownloadURL(fileRef)
        this.fileHandle = null
        if (Dateiart == 'Char') { this.charPDFurl = url }
        else if (Dateiart == 'Bild') { this.charPicurl = url }
        else if (Dateiart == 'PDF1') { this.charPDF1url = url }
        else if (Dateiart == 'PDF2') { this.charPDF2url = url }
      } catch (err) {
        console.error('Fehler beim Hochladen der PDF', err)
      }

      if (!this.$store.state.activeCharID) {
        console.error("Keine activeCharID verfügbar.")
        return
      }
      try {
        const docRef = doc(db, "Charaktere", this.$store.state.activeCharID)
        const payload = {
          pdfURL: this.charPDFurl,
          picURL: this.charPicurl,
        }
        // PDF1/PDF2 nur schreiben, wenn die Komponente diese Felder kennt (D&D).
        if (this.charPDF1url !== undefined) { payload.PDF1URL = this.charPDF1url }
        if (this.charPDF2url !== undefined) { payload.PDF2URL = this.charPDF2url }
        setDoc(docRef, payload, { merge: true })
      } catch (err) {
        console.error("Fehler beim Speichern des Dokuments: ", err)
      }
    },

    // Standard-Hook: Komponenten mit system-spezifischen Feldern überschreiben dies.
    applyCharData() {},
  },
}
